<template>
  <!--
    Three-column grid: [airline icons | dep/arr block | transfers / stops].
    The dep/arr block is itself a 3-column grid (dep | em-dash | arr) with
    fixed-width dep/arr columns sized to the `HH:mm` time slot. The city
    `<span>` under each time inherits that width and ellipsizes via
    `truncate` when it is wider — so longer city names never push into
    the transfers column.
  -->
  <div class="grid grid-cols-[auto_auto_1fr] items-start gap-x-3">
    <AirlineIconsGroup
      :airlines="uniqueAirlines"
      :airlines-to-display="2"
      is-vertical
    />

    <div class="grid grid-cols-[3.5rem_auto_3.5rem] items-center gap-x-1">
      <div class="text-text-neutral-primary flex min-w-0 flex-col text-left">
        <p
          class="h-fit whitespace-nowrap text-base"
        >
          {{ flightDepTime }}
        </p>
        <span class="text-situational-grey truncate text-xs">
          {{ depCityText }}
        </span>
      </div>
      <p class="text-situational-grey text-xs">—</p>
      <div class="flex min-w-0 flex-col text-right">
        <p
          class="text-text-neutral-primary h-fit whitespace-nowrap text-base"
        >
          {{ flightArrTime }}
        </p>
        <span class="text-situational-grey truncate text-xs">
          {{ arrCityText }}
        </span>
      </div>
    </div>

    <div class="min-w-0">
      <p class="text-text-neutral-primary mb-0.5 whitespace-nowrap text-sm">
        <span>
          {{ transfersText }}
        </span>
        /
        <span>
          {{ flight.durationText }}
        </span>
      </p>

      <div
        v-for="(segment, segmentIndex) in flight.segments"
        :key="segmentIndex"
      >
        <div v-if="segment.stops.count">
          <p
            v-for="location in segment.stops.locations"
            :key="location.airport"
            class="text-situational-grey text-xs"
          >
            {{ location.duration }} {{ $t('main.cities.stop') }}
          </p>
        </div>
        <p
          v-if="segmentsData.isFlightConnectionVisible(segmentIndex)"
          class="text-situational-grey text-xs"
        >
          {{
            getDurationBetweenSegments(
              segment.arr,
              segmentsData.getNextSegmentDep(segmentIndex),
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import type { Airflow } from '@aviata/platform-types';
  import { AirlineIconsGroup } from 'freedom-shared';
  import { ticketDetails, dateUtils } from 'shared';

  const props = defineProps<{
    flightIdx: number;
    flight: ticketDetails.types.MappedFlight;
    /** Used as the prefix for `data-qa-id`s emitted by this row. */
    source: string;
  }>();

  const { t } = useI18n();

  const firstSegment = computed(() => props.flight.segments[0]);
  const lastSegment = computed(() => props.flight.segments.slice(-1)[0]);
  const flightDepTime = computed(() => firstSegment.value.dep.time);
  const flightArrTime = computed(() => lastSegment.value.arr.time);
  /**
   * Resolved city name, falling back to airport iata if the dict could
   * not resolve a city. The recommended-offers card always wants the
   * city — that's the reason this component exists separately from
   * `SearchCardFlight`, which always shows airport iata.
   */
  const depCityText = computed(
    () => firstSegment.value.dep.city || firstSegment.value.dep.airport,
  );
  const arrCityText = computed(
    () => lastSegment.value.arr.city || lastSegment.value.arr.airport,
  );
  const numberOfTransfers = computed(() => props.flight.segments.length - 1);
  const transfersText = computed(() =>
    numberOfTransfers.value > 0
      ? t('general.transfers', numberOfTransfers.value)
      : t('general.direct'),
  );
  const uniqueAirlines = computed(() =>
    ticketDetails.lib.getUniqueAirlines(props.flight),
  );
  const segmentsData = computed(() =>
    ticketDetails.lib.getSegmentsData(props.flight),
  );

  function getDurationBetweenSegments(
    currentSegmentArr: Airflow.Segment.Point,
    nextSegmentDep: Airflow.Segment.Point | null,
  ) {
    if (!currentSegmentArr || !nextSegmentDep) {
      return;
    }

    const arrAt = new Date(currentSegmentArr.at);
    const depAt = new Date(nextSegmentDep.at);
    const durationInSeconds = (depAt.getTime() - arrAt.getTime()) / 1000;
    const duration = dateUtils.prettyDurationFromSeconds(durationInSeconds);
    const layoverCity =
      nextSegmentDep && 'city' in nextSegmentDep
        ? (nextSegmentDep as { city?: string }).city
        : undefined;

    return [duration, layoverCity].filter(part => part).join(' ');
  }
</script>
